import { toJpeg } from "html-to-image";
import jsPDF from "jspdf";

const PDF_APP_BOTTOM = "0";
/** Space above LeaseWorks card in PDF; must override Tailwind `!mt-[5.5rem]` on the card.
 * Matches the other tight bureau/PG gaps below so spacing is consistent within the page. */
const PDF_LEASEWORKS_MARGIN_TOP = "0.3rem";
/** Tighter top margin for bureau cards in PDF (replaces the default space-y-6 = 1.5rem gap) */
const PDF_BUREAU_MARGIN_TOP = "0.3rem";
/** Top padding on underwrite-decision and PG section in PDF */
const PDF_UNDERWRITE_PADDING_TOP = "0.3rem";
const PDF_PG_MARGIN_TOP = "0.3rem";

/**
 * Generates and downloads a PDF from HTML elements
 * @param elementIds Array of element IDs to capture (in order)
 * @param fileName Name of the PDF file (without .pdf extension)
 * @param backgroundColor Background color for the PDF
 * @param targetPixelWidth Width to normalize each capture when compositing (default 1100 * 2)
 */
export const generatePDF = async (
  elementIds: string[],
  fileName: string,
  backgroundColor: string = "#0C1017",
  targetPixelWidth: number = 1100 * 2,
  showWatermark: boolean = false,
): Promise<void> => {
  const pages = elementIds
    .map((id) => document.getElementById(id))
    .filter(Boolean) as HTMLElement[];

  if (pages.length === 0) {
    console.error("No elements found to capture for PDF");
    return;
  }

  const images: HTMLImageElement[] = [];
  const maxWidth = targetPixelWidth;
  const sectionGapPx = 5;
  const padding = 20;

  // Canvas-px height of one PDF page (US Letter), for snapping a section's top
  // edge to the start of the next page. Depends only on the fixed canvas width,
  // so it's available before any image is captured.
  const canvasWidthFull = maxWidth + 2 * padding;
  const PAGE_CANVAS_PX = (279.4 * canvasWidthFull) / 215.9;

  // Independent, hand-tunable top margin for each page-snap point (the first
  // page's own margin is `padding`, above, and is NOT touched by these). Adjust
  // each one on its own — they don't affect each other or the first page.
  const PDF_PAYNET_PAGE_MARGIN_PX = 70; // third-last page: -30%
  const PDF_UNDERWRITE_PAGE_MARGIN_PX = 12; // second-last page: -50% (from 30)
  const PDF_DATACHANGES_PAGE_MARGIN_PX = 8; // last page: -50% (from 30)

  for (let i = 0; i < pages.length; i++) {
    const el = pages[i];
    if (!el) continue;

    const originalHeight = el.style.height;
    const originalOverflow = el.style.overflow;
    const originalDisplay = el.style.display;

    if (
      originalHeight === "0px" ||
      el.style.height === "0" ||
      originalOverflow === "hidden" ||
      el.style.overflow === "hidden" ||
      originalDisplay === "none" ||
      el.style.display === "none"
    ) {
      el.style.height = "auto";
      el.style.overflow = "visible";
      if (originalDisplay === "none" || el.style.display === "none") {
        el.style.display = "block";
      }
    }

    const undoPdfPadding: Array<() => void> = [];

    if (el.id === "underwrite-decision") {
      const prevW = el.style.width;
      const prevMinW = el.style.minWidth;
      const prevMaxW = el.style.maxWidth;
      const prevGridCols = el.style.gridTemplateColumns;
      const prevClasses = el.className;

      el.style.setProperty("width", "1100px", "important");
      el.style.setProperty("min-width", "1100px", "important");
      el.style.setProperty("max-width", "1100px", "important");
      el.style.setProperty("grid-template-columns", "repeat(2, minmax(0, 1fr))", "important");

      const ptBefore = el.style.getPropertyValue("padding-top");
      el.style.setProperty("padding-top", PDF_UNDERWRITE_PADDING_TOP, "important");

      undoPdfPadding.push(() => {
        el.style.width = prevW;
        el.style.minWidth = prevMinW;
        el.style.maxWidth = prevMaxW;
        el.style.gridTemplateColumns = prevGridCols;
        el.className = prevClasses;
        if (ptBefore) el.style.setProperty("padding-top", ptBefore);
        else el.style.removeProperty("padding-top");
      });
    }

    if (el.id === "application-details") {
      const prevW = el.style.width;
      const prevMinW = el.style.minWidth;
      const prevMaxW = el.style.maxWidth;
      el.style.setProperty("width", "1100px", "important");
      el.style.setProperty("min-width", "1100px", "important");
      el.style.setProperty("max-width", "1100px", "important");
      undoPdfPadding.push(() => {
        el.style.width = prevW;
        el.style.minWidth = prevMinW;
        el.style.maxWidth = prevMaxW;
      });

      const pbBefore = el.style.getPropertyValue("padding-bottom");
      el.style.setProperty("padding-bottom", PDF_APP_BOTTOM);
      undoPdfPadding.push(() => {
        if (pbBefore) el.style.setProperty("padding-bottom", pbBefore);
        else el.style.removeProperty("padding-bottom");
      });

      const leaseCard = el.querySelector<HTMLElement>("#pdf-leaseworks-card");
      if (leaseCard) {
        const mtBefore = leaseCard.style.getPropertyValue("margin-top");
        const mtPriority = leaseCard.style.getPropertyPriority("margin-top");
        leaseCard.style.setProperty("margin-top", PDF_LEASEWORKS_MARGIN_TOP, "important");
        undoPdfPadding.push(() => {
          if (mtBefore) leaseCard.style.setProperty("margin-top", mtBefore, mtPriority || "");
          else leaseCard.style.removeProperty("margin-top");
        });

      }

      for (const bureauId of ["#pdf-dnb-card", "#pdf-experian-card"]) {
        const bureauCard = el.querySelector<HTMLElement>(bureauId);
        if (bureauCard) {
          const mtBefore = bureauCard.style.getPropertyValue("margin-top");
          const mtPriority = bureauCard.style.getPropertyPriority("margin-top");
          bureauCard.style.setProperty("margin-top", PDF_BUREAU_MARGIN_TOP, "important");
          undoPdfPadding.push(() => {
            if (mtBefore) bureauCard.style.setProperty("margin-top", mtBefore, mtPriority || "");
            else bureauCard.style.removeProperty("margin-top");
          });
        }
      }

      for (const [selector, value] of [
        ["#pdf-score-section", PDF_PG_MARGIN_TOP],
        ["#pdf-corp-scoring", PDF_PG_MARGIN_TOP],
      ] as [string, string][]) {
        const card = el.querySelector<HTMLElement>(selector);
        if (card) {
          const mtBefore = card.style.getPropertyValue("margin-top");
          const mtPriority = card.style.getPropertyPriority("margin-top");
          card.style.setProperty("margin-top", value, "important");
          undoPdfPadding.push(() => {
            if (mtBefore) card.style.setProperty("margin-top", mtBefore, mtPriority || "");
            else card.style.removeProperty("margin-top");
          });
        }
      }

      // PayNet starts a fresh PDF page (LeaseWorks + Personal Guarantor Details
      // then flow naturally after it on that same page). Rather than a fixed rem
      // value — which only holds until the cards above it change height, as just
      // happened when Pass Score was added — measure PayNet's actual position
      // (after the D&B/Experian tightening above has been applied) and insert
      // exactly enough top margin to land it on the next page boundary. CSS px
      // measured here map to canvas px at the same 2x pixelRatio toJpeg captures
      // at, since this element's width is pinned to `maxWidth` above.
      const paynetCard = el.querySelector<HTMLElement>("#pdf-paynet-card");
      if (paynetCard) {
        const mtBefore = paynetCard.style.getPropertyValue("margin-top");
        const mtPriority = paynetCard.style.getPropertyPriority("margin-top");

        const containerTop = el.getBoundingClientRect().top;
        const naturalTopCanvasPx =
          padding + (paynetCard.getBoundingClientRect().top - containerTop) * 2;
        const nextBoundary =
          Math.ceil((naturalTopCanvasPx + 1) / PAGE_CANVAS_PX) * PAGE_CANVAS_PX +
          PDF_PAYNET_PAGE_MARGIN_PX;
        const neededGapCssPx = Math.max(0, nextBoundary - naturalTopCanvasPx) / 2;

        paynetCard.style.setProperty("margin-top", `${neededGapCssPx}px`, "important");
        undoPdfPadding.push(() => {
          if (mtBefore) paynetCard.style.setProperty("margin-top", mtBefore, mtPriority || "");
          else paynetCard.style.removeProperty("margin-top");
        });
      }
    }

    let dataUrl: string;
    try {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      dataUrl = await toJpeg(el, {
        backgroundColor,
        cacheBust: true,
        pixelRatio: 2,
        quality: 0.92,
      });
    } finally {
      undoPdfPadding.reverse().forEach((fn) => fn());
      el.style.height = originalHeight;
      el.style.overflow = originalOverflow;
      el.style.display = originalDisplay;
    }

    const img = new window.Image();
    img.src = dataUrl;

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Failed to load captured image"));
    });

    images.push(img);
  }

  if (images.length === 0) {
    return;
  }

  const scaledHeights = images.map(
    (img) => (img.height / img.width) * maxWidth,
  );

  // Snap underwrite-decision (image[1] — "Internal Rating / Athena Recommendations")
  // to the next PDF page boundary, after application-details.
  let pageAlignGap0 = 0;
  if (scaledHeights.length >= 2) {
    const naturalStart1 = padding + scaledHeights[0] + sectionGapPx;
    const nextBoundary0 =
      Math.ceil((naturalStart1 + 1) / PAGE_CANVAS_PX) * PAGE_CANVAS_PX +
      PDF_UNDERWRITE_PAGE_MARGIN_PX;
    pageAlignGap0 = Math.max(0, nextBoundary0 - naturalStart1);
  }

  // Snap the data-changes table (image[2]) to the next PDF page boundary, after
  // underwrite-decision (accounting for the gap just inserted before it).
  let pageAlignGap = 0;
  if (scaledHeights.length >= 3) {
    const naturalStart2 =
      padding + scaledHeights[0] + sectionGapPx + pageAlignGap0 + scaledHeights[1] + sectionGapPx;
    const nextBoundary =
      Math.ceil((naturalStart2 + 1) / PAGE_CANVAS_PX) * PAGE_CANVAS_PX +
      PDF_DATACHANGES_PAGE_MARGIN_PX;
    pageAlignGap = Math.max(0, nextBoundary - naturalStart2);
  }

  const totalImageHeight =
    scaledHeights.reduce((a, b) => a + b, 0) +
    sectionGapPx * Math.max(0, images.length - 1) +
    pageAlignGap0 +
    pageAlignGap;

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidthFull;
  canvas.height = totalImageHeight + padding * 2;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    console.error("Failed to get canvas context");
    return;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const bgColorRgb = parseColorToRgb(backgroundColor);
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let offset = padding;
  images.forEach((img, index) => {
    const drawW = maxWidth;
    const drawH = (img.height / img.width) * maxWidth;
    if (index === 1) offset += pageAlignGap0;
    if (index === 2) offset += pageAlignGap;
    ctx.drawImage(img, padding, offset, drawW, drawH);
    offset += drawH;
    if (index < images.length - 1) {
      offset += sectionGapPx;
    }
  });

  if (showWatermark) {
    ctx.save();
    ctx.globalAlpha = 0.13;
    const fontSize = Math.round(canvasWidthFull * 0.028);
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.fillStyle = "#d0d0d0";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const text = "USE FOR TRAINING ONLY";
    const angleRad = -Math.PI / 4;
    const xStep = Math.round(canvasWidthFull * 0.55);
    const yStep = Math.round(canvasWidthFull * 0.32);
    for (let y = 0; y < canvas.height + yStep; y += yStep) {
      const rowIndex = Math.round(y / yStep);
      const xOffset = rowIndex % 2 === 0 ? 0 : xStep / 2;
      for (let x = -xStep + xOffset; x < canvasWidthFull + xStep; x += xStep) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angleRad);
        ctx.fillText(text, 0, 0);
        ctx.restore();
      }
    }
    ctx.restore();
  }

  const pdf = new jsPDF("p", "mm", "letter");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const combinedDataUrl = canvas.toDataURL("image/jpeg", 0.92);

  const imgWidthMm = pdfWidth;
  const imgHeightMm = (canvas.height * pdfWidth) / canvas.width;

  let position = 0;
  while (position < imgHeightMm) {
    if (bgColorRgb) {
      pdf.setFillColor(bgColorRgb.r, bgColorRgb.g, bgColorRgb.b);
    }
    pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
    pdf.addImage(
      combinedDataUrl,
      "JPEG",
      0,
      -position,
      imgWidthMm,
      imgHeightMm,
    );
    position += pdfHeight;
    if (position < imgHeightMm) {
      pdf.addPage();
    }
  }

  pdf.save(`${fileName}.pdf`);
};

const parseColorToRgb = (
  color: string,
): { r: number; g: number; b: number } | null => {
  const hexMatch = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
  if (hexMatch) {
    return {
      r: parseInt(hexMatch[1], 16),
      g: parseInt(hexMatch[2], 16),
      b: parseInt(hexMatch[3], 16),
    };
  }

  const hslMatch = /hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/i.exec(
    color.trim(),
  );
  if (hslMatch) {
    const h = parseInt(hslMatch[1], 10) / 360;
    const s = parseInt(hslMatch[2], 10) / 100;
    const l = parseInt(hslMatch[3], 10) / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
    const m = l - c / 2;

    let r = 0,
      g = 0,
      b = 0;

    if (h * 6 < 1) {
      r = c;
      g = x;
      b = 0;
    } else if (h * 6 < 2) {
      r = x;
      g = c;
      b = 0;
    } else if (h * 6 < 3) {
      r = 0;
      g = c;
      b = x;
    } else if (h * 6 < 4) {
      r = 0;
      g = x;
      b = c;
    } else if (h * 6 < 5) {
      r = x;
      g = 0;
      b = c;
    } else {
      r = c;
      g = 0;
      b = x;
    }

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255),
    };
  }

  return null;
};
